/**
 * Deep-link builders from the analytics explorer to the canonical source
 * sections, carrying the active filter as query params. Routes verified against
 * `frontend-admin/src/App.tsx` + `pages/billing/routes.tsx`:
 *  - children/contracts/employees use the `:id/edit` page as their detail view,
 *  - invoices focus via `?invoice_id=`, reconciliation via `?tab=…&debtor=`,
 *  - calendar opens an event via `?eventId=`.
 *
 * Which destinations actually CONSUME url filter params (verified against the
 * target pages -- do NOT add cosmetic params to the others):
 *  - kitchen (`/kitchen`, KitchenViewPage): `month`, `groupIds`, `childcareIds`
 *    (the two id params are comma-joined lists, split on `,` on read).
 *  - invoices (`/billing/invoices`): `year`, `month`, `status`, `child_id`.
 *  - reconciliation (`/billing/reconciliation`): `tab`, `debtor`.
 *  - calendar (`/calendar`): `eventId`.
 * The remaining list pages -- `/activities`, `/workflows`, `/courses`,
 * `/children`, `/contracts`, `/employees`, `/shifts`, `/time-tracking`,
 * `/settling-in` -- read NO url filter params today, so their builders navigate
 * to the section only (section-level deep link). A follow-up could make those
 * pages url-aware; until then carried params would be silently ignored.
 */

import { isReconciliationEnabled } from "@/lib/featureFlags";

type QueryValue = string | number | boolean | null | undefined;

/** Appends a query string to `path`, dropping empty/null/undefined values. */
export function withQuery(
  path: string,
  query: Record<string, QueryValue> = {},
): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === "") continue;
    params.set(key, String(value));
  }
  const qs = params.toString();
  return qs ? `${path}?${qs}` : path;
}

export interface ScopeFilters {
  /** Childcare id (maps to nothing on most list pages yet, but carried). */
  childcare?: string;
  /** Group id. */
  group?: string;
  /** Free-text search. */
  search?: string;
  /** Status filter (CSV where the target supports it). */
  status?: string;
}

// --- Children -------------------------------------------------------------

export function childrenListUrl(f: ScopeFilters = {}): string {
  return withQuery("/children", {
    group_id: f.group,
    status: f.status,
    search: f.search,
  });
}

export function childProfileUrl(id: string): string {
  return `/children/${encodeURIComponent(id)}/edit`;
}

// --- Contracts ------------------------------------------------------------

export interface ContractsFilters extends ScopeFilters {
  childId?: string;
  startFrom?: string;
  startTo?: string;
  endFrom?: string;
  endTo?: string;
}

export function contractsListUrl(f: ContractsFilters = {}): string {
  return withQuery("/contracts", {
    status: f.status,
    child_id: f.childId,
    search: f.search,
    start_date_from: f.startFrom,
    start_date_to: f.startTo,
    end_date_from: f.endFrom,
    end_date_to: f.endTo,
  });
}

export function contractDetailUrl(id: string): string {
  return `/contracts/${encodeURIComponent(id)}/edit`;
}

// --- Billing / Finance ----------------------------------------------------

// Both debtor targets live in the reconciliation workspace. With
// VITE_RECONCILIATION_ENABLED off its routes are not mounted, and the invoices
// list reads no debtor filter, so there is no target: the builders return null
// and the caller renders no link (#1878).

export function debtorDetailUrl(id: string): string | null {
  if (!isReconciliationEnabled()) return null;
  return `/billing/reconciliation?tab=debtor-accounts&debtor=${encodeURIComponent(id)}`;
}

/** The debtor-accounts list view of the reconciliation workspace. */
export function reconciliationDebtorsUrl(): string | null {
  if (!isReconciliationEnabled()) return null;
  return "/billing/reconciliation?tab=debtor-accounts";
}

export interface InvoicesFilters extends ScopeFilters {
  year?: string | number;
  month?: string | number;
  childId?: string;
}

export function invoicesListUrl(f: InvoicesFilters = {}): string {
  return withQuery("/billing/invoices", {
    year: f.year,
    month: f.month,
    status: f.status,
    search: f.search,
    child_id: f.childId,
  });
}

export function invoiceDetailUrl(id: string): string {
  return `/billing/invoices?invoice_id=${encodeURIComponent(id)}`;
}

// --- Staff ----------------------------------------------------------------

export function employeesListUrl(f: ScopeFilters = {}): string {
  return withQuery("/employees", { search: f.search });
}

export function employeeProfileUrl(id: string): string {
  return `/employees/${encodeURIComponent(id)}/edit`;
}

export function shiftsScheduleUrl(f: { tab?: string } = {}): string {
  return withQuery("/shifts", { tab: f.tab });
}

export function timeTrackingUrl(): string {
  return "/time-tracking";
}

// --- Operations -----------------------------------------------------------

export function activitiesListUrl(): string {
  return "/activities";
}

export function coursesUrl(): string {
  return "/courses";
}

export function workflowsListUrl(): string {
  return "/workflows";
}

export function eventDetailUrl(id: string): string {
  return `/calendar?eventId=${encodeURIComponent(id)}`;
}

export function calendarUrl(): string {
  return "/calendar";
}

/**
 * Filters consumed by the Kitchen View page. `month` is a `YYYY-MM` string;
 * `groupIds`/`childcareIds` are comma-joined id lists (the page parses them by
 * splitting on `,`). All optional -- `kitchenUrl()` with no args still works.
 */
export interface KitchenFilters {
  month?: string;
  groupIds?: string;
  childcareIds?: string;
}

export function kitchenUrl(f: KitchenFilters = {}): string {
  return withQuery("/kitchen", {
    month: f.month,
    groupIds: f.groupIds,
    childcareIds: f.childcareIds,
  });
}

export function settlingInListUrl(): string {
  return "/settling-in";
}

export function settlingInDetailUrl(id: string): string {
  return `/settling-in/${encodeURIComponent(id)}`;
}
